import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from "redux-saga";

import authSlice from '../slice/authSlice';
import userSlice from '../slice/userSlice';
import contactusSlice from '../slice/contactusSlice';

import bannerReducer from '../slice/bannerSlice';
import khajurahoPackageReducer from '../slice/khajurahoPackageSlice';
import enquiryReducer from '../slice/enquirySlice';
import videoReducer from '../slice/videoSlice';
import testimonialReducer from '../slice/testimonialSlice';
import visitReducer from '../slice/visitSlice';
import galleryReducer from '../slice/gallerySlice';
import bookingReducer from '../slice/bookingSlice';
import serviceReducer from '../slice/serviceSlice';

import rootSaga from '../apis/rootSaga';

// ─── LocalStorage Persistence ──────────────────────────────────────────────
// This makes pages show their content INSTANTLY on revisit
// instead of showing blank until API responds.

const CACHE_KEY = 'khajuraho_state_cache';
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const loadState = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const { data, ts } = JSON.parse(raw);
    // Invalidate if older than TTL
    if (Date.now() - ts > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return undefined;
    }
    return data;
  } catch {
    return undefined;
  }
};

const saveState = (state) => {
  try {
    // Only persist public data slices — never auth tokens
    const toSave = {
      banner: state.banner,
      khajurahoPackage: { ...state.khajurahoPackage, oneData: null }, // don't persist single item
      visit: state.visit,
      gallery: state.gallery,
      service: state.service,
      testimonial: state.testimonial,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: toSave, ts: Date.now() }));
  } catch {
    // Ignore write errors (storage full etc.)
  }
};

// ───────────────────────────────────────────────────────────────────────────

const sagaMiddleware = createSagaMiddleware();

const preloadedState = loadState();

const store = configureStore({
  reducer: {
    auth: authSlice,
    user: userSlice,
    contact: contactusSlice,
    banner: bannerReducer,
    khajurahoPackage: khajurahoPackageReducer,
    enquiry: enquiryReducer,
    video: videoReducer,
    testimonial: testimonialReducer,
    visit: visitReducer,
    gallery: galleryReducer,
    booking: bookingReducer,
    service: serviceReducer,
  },
  preloadedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(sagaMiddleware),
});

// Save to localStorage whenever state changes (throttled)
let saveTimeout = null;
store.subscribe(() => {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => saveState(store.getState()), 500);
});

sagaMiddleware.run(rootSaga);

export default store;

